import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "./csaApi";
import CsaAssign from "./CsaAssign";
import "./CsaDashboard.css";

/**
 * The Project Manager's schedule and load view (design §7d).
 *
 * A PM sees **every** auditor, not only their own team — §7d: otherwise
 * conflicts across teams stay invisible, which is the main thing the role
 * exists to prevent.
 *
 * Two things get prominence over the calendar itself, because they are the two
 * that need somebody to act:
 *
 *   · **Audits with nobody on them.** Usually because a PM took their auditor
 *     for something else, which §7d insists must be visible rather than silent.
 *   · **Double-bookings that already exist.** Somebody is in two plants at
 *     once, and the sooner that is on screen the cheaper it is to fix.
 *
 * The load view is deliberately not a utilisation percentage. The system knows
 * audit dates and nothing else — no working-day calendar, no leave, no travel —
 * so any percentage of capacity would be invented. Bars are scaled to the
 * busiest person in the period, which compares people to each other rather than
 * to a target that does not exist.
 */

const PERIODS = [
  { key: "month", label: "This month" },
  { key: "quarter", label: "3 months" },
  { key: "half", label: "6 months" },
  { key: "year", label: "A year" },
];

export default function CsaSchedule() {
  const [tab, setTab] = useState("schedule");
  const [from, setFrom] = useState(() => monthStart());
  // The last day of the third month, not the first day of the fourth. The old
  // default gave three months and one day, and that stray day rendered as a
  // one-day December band whose label had nowhere to go.
  const [to, setTo] = useState(() => monthEnd(2));
  const [period, setPeriod] = useState("quarter");

  const [sched, setSched] = useState(null);
  const [load, setLoad] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  /*
   * The audit being reassigned, if any. A PM seeing a clash on this screen had
   * no way to act on it until this existed — the endpoint was reachable only
   * from a terminal.
   */
  const [assigning, setAssigning] = useState(null);

  const loadSchedule = useCallback(() => {
    setBusy(true);
    setError(null);
    api
      .scheduleFor(from, to)
      .then(setSched)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [from, to]);

  const loadLoad = useCallback(() => {
    setBusy(true);
    setError(null);
    api
      .scheduleLoad(period, from.slice(0, 7))
      .then(setLoad)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [period, from]);

  useEffect(() => {
    if (tab === "schedule") loadSchedule();
    else loadLoad();
  }, [tab, loadSchedule, loadLoad]);

  /* A row of days, so a bar can be drawn against a common axis. */
  const span = useMemo(() => Math.max(1, daysBetween(from, to)), [from, to]);

  /** Where a bar sits, as a percentage of the window. */
  const place = useCallback(
    (a) => {
      const s = Math.max(0, daysBetween(from, a.start_date) - 1);
      const e = Math.min(span, daysBetween(from, a.end_date));
      return { left: (s / span) * 100, width: Math.max(1.2, ((e - s) / span) * 100) };
    },
    [from, span]
  );

  /*
   * The months in the window, as bands across the same axis the bars use.
   *
   * Without this the bars were positioned correctly against nothing — you could
   * see that two audits did not overlap but not when either of them was. Months
   * are unequal widths because they are unequal lengths, which is the honest
   * way to draw a date axis and the reason a fixed twelve-column grid would
   * misplace every bar by a day or two.
   */
  const months = useMemo(() => {
    const out = [];
    let cur = from.slice(0, 8) + "01";
    while (cur <= to) {
      const next = addMonths(cur, 1);
      const bandStart = cur < from ? from : cur;
      const bandEnd = next > to ? to : addDays(next, -1);
      if (bandEnd >= bandStart) {
        const l = (Math.max(0, daysBetween(from, bandStart) - 1) / span) * 100;
        const w = (daysBetween(bandStart, bandEnd) / span) * 100;
        out.push({
          key: cur,
          label: monthLabel(cur),
          left: l,
          width: w,
          // A narrow band cannot hold "September 2026", so it gets "Sep".
          short: w < 12,
          // And a sliver cannot hold even that. It keeps its gridline and its
          // tooltip; a clipped "D" told nobody anything.
          tooNarrow: w < 4,
        });
      }
      cur = next;
    }
    return out;
  }, [from, to, span]);

  /** Today, if it falls inside the window — the line a PM actually looks for. */
  const today = useMemo(() => {
    const now = new Date().toISOString().slice(0, 10);
    if (now < from || now > to) return null;
    return ((daysBetween(from, now) - 1) / span) * 100;
  }, [from, to, span]);

  if (error) return <div className="csa-dash"><p className="cd-error">{error}</p></div>;

  return (
    <div className="csa-dash">
      <div className="cd-head">
        <div className="sch-tabs">
          <button
            type="button"
            className={"sch-tab" + (tab === "schedule" ? " on" : "")}
            onClick={() => setTab("schedule")}
          >
            Schedule
          </button>
          <button
            type="button"
            className={"sch-tab" + (tab === "load" ? " on" : "")}
            onClick={() => setTab("load")}
          >
            Auditor load
          </button>
        </div>

        {tab === "schedule" ? (
          <>
            <label htmlFor="sch-from">From</label>
            <input id="sch-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <label htmlFor="sch-to">To</label>
            <input id="sch-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </>
        ) : (
          <>
            <label htmlFor="sch-period">Period</label>
            <select id="sch-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            <label htmlFor="sch-month">From</label>
            <input
              id="sch-month"
              type="month"
              value={from.slice(0, 7)}
              onChange={(e) => setFrom(e.target.value + "-01")}
            />
          </>
        )}
      </div>

      {busy ? <p className="cd-empty">Loading…</p> : null}

      {/* ---------------- schedule ---------------- */}
      {tab === "schedule" && sched && !busy ? (
        <>
          {/* The two things that need somebody to act, before the calendar. */}
          {sched.existing_conflicts && sched.existing_conflicts.length ? (
            <div className="sch-alert bad">
              <strong>
                {sched.existing_conflicts.length} double-booking
                {sched.existing_conflicts.length === 1 ? "" : "s"} in this window
              </strong>
              <ul>
                {sched.existing_conflicts.map((c, i) => (
                  <li key={i}>
                    <b>{c.name}</b> is on {c.a} and {c.b} at the same time —{" "}
                    {c.overlap_from} to {c.overlap_to}
                    {c.booked_by_a || c.booked_by_b ? (
                      <span className="sch-owner">
                        booked by {[c.booked_by_a, c.booked_by_b].filter(Boolean).join(" and ")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {sched.unassigned && sched.unassigned.length ? (
            <div className="sch-alert warn">
              <strong>
                {sched.unassigned.length} audit
                {sched.unassigned.length === 1 ? " has" : "s have"} nobody on them
              </strong>
              <ul>
                {sched.unassigned.map((a) => (
                  <li key={a.audit_id}>
                    {a.organization} · {a.site} — {a.start_date} to {a.end_date}
                    {a.owned_by ? <span className="sch-owner">booked by {a.owned_by}</span> : null}
                    <button type="button" className="sch-assign" onClick={() => setAssigning(a)}>
                      Assign
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <section className="cd-card">
            <h3>
              {from} to {to}
            </h3>

            {!sched.auditors.length ? (
              <p className="cd-empty">Nobody is booked in this window.</p>
            ) : (
              <div className="sch-grid sch-scroll">
                {/* ---- the axis ---- */}
                <div className="sch-row sch-axis">
                  <div className="sch-name" />
                  <div className="sch-track axis">
                    {months.map((m) => (
                      <div
                        key={m.key}
                        className="sch-month"
                        style={{ left: m.left + "%", width: m.width + "%" }}
                        title={monthLabel(m.key)}
                      >
                        {m.tooNarrow ? null : (
                          <span>{m.short ? m.label.slice(0, 3) : m.label}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sch-rows">
                  {sched.auditors.map((p) => (
                    <div className="sch-row" key={p.user_id}>
                      <div className="sch-name">
                        <span className="sch-who">{p.name}</span>
                        <span className="sch-count">{p.audits.length}</span>
                      </div>
                      <div className="sch-track">
                        {/* Month boundaries behind the bars, so a bar can be
                            read against the axis above it. */}
                        {months.map((m) => (
                          <i key={m.key} className="sch-gridline" style={{ left: m.left + "%" }} />
                        ))}
                        {today !== null ? (
                          <i className="sch-today" style={{ left: today + "%" }} title="today" />
                        ) : null}

                        {p.audits.map((a) => {
                          const pos = place(a);
                          return (
                            <button
                              type="button"
                              key={a.audit_id}
                              className={"sch-bar s-" + a.status}
                              style={{ left: pos.left + "%", width: pos.width + "%" }}
                              title={`${a.organization} · ${a.site}\n${a.start_date} to ${a.end_date}\n${a.status}\n\nClick to reassign`}
                              onClick={() => setAssigning(a)}
                            >
                              <span>{a.site}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="cd-foot">
              Months are drawn to their real lengths, so a bar's position and
              width are its actual dates. The red line is today. Hover a bar for
              the client, site and dates.
            </p>
          </section>
        </>
      ) : null}

      {assigning ? (
        <>
          <div className="csa-pad-scrim" onClick={() => setAssigning(null)} />
          <CsaAssign
            audit={assigning}
            onCancel={() => setAssigning(null)}
            onDone={() => {
              setAssigning(null);
              loadSchedule();
            }}
          />
        </>
      ) : null}

      {/* ---------------- load ---------------- */}
      {tab === "load" && load && !busy ? (
        <section className="cd-card">
          <h3>
            {load.from} to {load.to}
          </h3>

          {!load.auditors.length ? (
            <p className="cd-empty">Nobody is booked in this period.</p>
          ) : (
            <table className="cd-move">
              <thead>
                <tr>
                  <th>Auditor</th>
                  <th className="n">Days</th>
                  <th className="n">Audits</th>
                  <th>Relative load</th>
                </tr>
              </thead>
              <tbody>
                {load.auditors.map((a) => (
                  <tr key={a.user_id}>
                    <td>{a.name}</td>
                    <td className="n">{a.days}</td>
                    <td className="n">{a.audits}</td>
                    <td>
                      <div className="sch-loadbar">
                        <i style={{ width: a.share_of_busiest + "%" }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Said on screen, not only in the API. */}
          <p className="cd-foot">{load.note}</p>
        </section>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** The last day of the month `n` months ahead — so a window covers whole months. */
function monthEnd(n = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + n + 1);
  d.setDate(0); // day 0 of the next month is the last day of this one
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthStart(addMonths = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + addMonths);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

function monthLabel(iso) {
  const [y, m] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

function addMonths(iso, n) {
  const [y, m] = iso.split("-").map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}-01`;
}

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Inclusive, so a one-day audit is one day. */
function daysBetween(a, b) {
  const ms = new Date(b + "T00:00:00Z") - new Date(a + "T00:00:00Z");
  return Math.round(ms / 86400000) + 1;
}
